import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { Alert, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ory } from 'src/auth/ory';
import { Logger } from 'src/utils/Logger';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';
import { useInviteSettingsFlow } from 'src/auth/hooks/useInviteSettingsFlow';
import { useAuthMethodCheck } from 'src/auth/hooks/useAuthMethodCheck';
import { WelcomeSection } from 'src/auth/invite/WelcomeSection';
import { ExpiredInviteSection } from 'src/auth/invite/ExpiredInviteSection';
import { InviteRegistrationForm } from 'src/auth/invite/InviteRegistrationForm';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { SuccessMessage } from 'src/auth/components/SuccessMessage';
import { setInviteEmail } from 'src/auth/utils/inviteStorage';
import { filterOidcNodesByAuthMethod, isSsoOnly } from 'src/auth/utils/ory-helpers';
import { AuthMethod } from 'src/auth/settings/types';

export const InvitePageRoute = () => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const isExpired = searchParams.get('expired') === 'true';

  if (isExpired) {
    return <ExpiredInvitePage />;
  }

  if (flowId) {
    return <InvitePageWithFlowId flowId={flowId} />;
  }

  return <InvitePageWithoutFlow />;
};

const ExpiredInvitePage = () => {
  const handleLoginClick = () => {
    window.location.replace('/login');
  };

  return (
    <AuthPageLayout withHeader>
      <ExpiredInviteSection onLoginClick={handleLoginClick} />
    </AuthPageLayout>
  );
};

const InvitePageWithFlowId = ({ flowId }: { flowId: string }) => {
  const authState = useAuthMethodCheck(flowId);

  if (authState === 'loading') {
    return <AuthPageLayout isLoading />;
  }

  if (authState === 'existing_user') {
    return <Navigate to="/login" replace />;
  }

  return <InvitePage flowId={flowId} />;
};

const InvitePageWithoutFlow = () => {
  const creatingFlowRef = useRef(false);

  useEffect(() => {
    if (!creatingFlowRef.current) {
      creatingFlowRef.current = true;
      ory
        .createBrowserSettingsFlow()
        .then(({ data }) => {
          window.location.replace(`/invite?flow=${data.id}`);
        })
        .catch(error => {
          Logger.error('Failed to create settings flow for invite page', error);
          window.location.replace('/login');
        });
    }
  }, []);

  return <AuthPageLayout isLoading />;
};

interface InvitePageProps {
  flowId: string;
}

const InvitePage = ({ flowId }: InvitePageProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    isLoading: isFlowLoading,
    error,
    userEmail,
    firstName,
    inviterName,
    tenantName,
    oidcNodes,
    csrfToken,
    formAction,
    updatePassword,
  } = useInviteSettingsFlow(flowId);

  const {
    config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = usePublicTenantConfig(tenantName || undefined);

  const isLoading = isFlowLoading || isConfigLoading;
  const variant: AuthMethod = !isConfigError && config?.authMethod ? config.authMethod : 'password';

  const filteredOidcNodes = useMemo(
    () => filterOidcNodesByAuthMethod(oidcNodes, variant),
    [oidcNodes, variant],
  );

  const handleCreateAccount = async (password: string) => {
    setIsSubmitting(true);
    const { success } = await updatePassword(password);
    setIsSubmitting(false);

    if (success) {
      setInviteEmail(userEmail);
      setShowSuccess(true);
    }
  };

  const handleSuccessRedirect = async () => {
    try {
      const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
      await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
    } catch (logoutError) {
      Logger.error(
        'Logout failed after credential setup, redirecting to login anyway',
        logoutError,
      );
    }
    window.location.replace('/login');
  };

  return (
    <AuthPageLayout isLoading={isLoading} withHeader>
      {isConfigError ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" w={450}>
          Something went wrong. Please try refreshing the page.
        </Alert>
      ) : showSuccess ? (
        <SuccessMessage variant="invite" onAction={handleSuccessRedirect} />
      ) : (
        <Stack align="center" gap="xl">
          {!showForm && (
            <WelcomeSection
              firstName={firstName}
              inviterName={inviterName}
              userEmail={userEmail}
              error={error}
              onAcceptInvite={() => setShowForm(true)}
              variant={variant}
              oidcNodes={filteredOidcNodes}
              formAction={formAction}
              csrfToken={csrfToken}
            />
          )}

          {showForm && !isSsoOnly(variant) && (
            <InviteRegistrationForm
              userEmail={userEmail}
              error={error}
              isSubmitting={isSubmitting}
              onSubmit={handleCreateAccount}
              oidcNodes={oidcNodes}
              formAction={formAction}
              csrfToken={csrfToken}
            />
          )}
        </Stack>
      )}
    </AuthPageLayout>
  );
};
